import React, { useState, useEffect, useCallback } from 'react';
import PartnerLayout from '@/components/layouts/PartnerLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePartner, API } from '@/contexts/PartnerContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Handshake, DollarSign, TrendingUp,
  ArrowDownToLine, Clock, CheckCircle2, Copy, Check,
  BarChart2, Loader2, ExternalLink, Percent
} from 'lucide-react';

const Stat = ({ icon: Icon, label, value, sub, color = 'text-foreground' }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <p className={`text-xl font-bold ${color}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const TYPE_LABEL = { subscription: 'Monthly Fee', transaction_fee: 'TX Fee' };
const LEVEL_COLOR = { 1: 'text-emerald-400 bg-emerald-500/15', 2: 'text-blue-400 bg-blue-500/15' };

export default function PartnerDashboard() {
  const { headers, partner } = usePartner();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/partner/dashboard`, { headers });
      setStats(data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const referralUrl = stats?.referral_code
    ? `https://coinpayup.com/register?ref=${stats.referral_code}`
    : '';

  const copyRef = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <PartnerLayout title="Dashboard">
      <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>
    </PartnerLayout>
  );

  return (
    <PartnerLayout title="Dashboard">
      <div className="space-y-6">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Users}         label="Total Customers"       value={stats?.total_customers || 0}        color="text-foreground" />
          <Stat icon={Handshake}     label="Total Sub-Partners"    value={stats?.total_sub_partners || 0}     color="text-blue-400" />
          <Stat icon={BarChart2}     label="Total Transactions"    value={stats?.total_transactions || 0}     color="text-amber-400" />
          <Stat icon={DollarSign}    label="Total Revenue"         value={`$${(stats?.total_revenue || 0).toFixed(2)}`}  color="text-foreground" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Stat icon={TrendingUp}     label="Commission Earned"  value={`$${(stats?.commission_earned || 0).toFixed(2)}`}  color="text-emerald-400" sub={`${stats?.commission_rate || 0}% base rate`} />
          <Stat icon={Clock}          label="Pending Commission" value={`$${(stats?.commission_pending || 0).toFixed(2)}`} color="text-amber-400" sub="Awaiting withdrawal" />
          <Stat icon={CheckCircle2}   label="Paid Commission"    value={`$${(stats?.commission_paid || 0).toFixed(2)}`}   color="text-primary" sub="Successfully withdrawn" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Stat icon={Percent} label="Monthly Commission Rate" value={`${stats?.monthly_commission_rate ?? stats?.commission_rate ?? 0}%`} color="text-blue-400" sub="Applied to subscription fees" />
          <Stat icon={Percent} label="TX Commission Rate"      value={`${stats?.tx_commission_rate ?? 0}%`}                              color="text-purple-400" sub="Applied to transaction fees" />
        </div>

        {/* Referral URL */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <ExternalLink className="h-4 w-4 text-primary" /> Your Referral Link
            </h3>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm font-mono text-muted-foreground truncate">
                {referralUrl || '—'}
              </div>
              <Button size="sm" variant="outline" onClick={copyRef} className="shrink-0" data-testid="copy-referral-btn">
                {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Share this link with merchants. When they sign up, they'll be linked to your account automatically.
            </p>
          </CardContent>
        </Card>

        {/* Recent Commissions */}
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold mb-4">Recent Commissions</h3>
            {!stats?.recent_commissions?.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No commissions yet</p>
            ) : (
              <div className="space-y-2">
                {stats.recent_commissions.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2.5">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${LEVEL_COLOR[c.level] || 'text-muted-foreground bg-muted'}`}>
                        L{c.level}
                      </span>
                      <div>
                        <p className="text-xs font-medium">{TYPE_LABEL[c.source_type] || c.source_type}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {new Date(c.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-emerald-400">+${c.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PartnerLayout>
  );
}
